import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePage } from "@/components/ServicePage";
import {
  MapPin,
  Star,
  Camera,
  MessageSquare,
  BarChart3,
  ShieldCheck,
  Wrench,
  Truck,
  Home as HomeIcon,
  Hammer,
  Trees,
  Zap,
  ArrowRight,
} from "lucide-react";
import { SERVICE_PRICING } from "@/lib/pricingData";
import { CASE_STUDIES } from "@/lib/caseStudiesData";
import { CLIENT_REVIEWS } from "@/lib/clientReviews";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";

const pricing = SERVICE_PRICING.find((s) => s.slug === "google-maps");
const OG_IMAGE = `https://coloradowebimpressions.com${ogImageFor({
  service: "google-maps",
  city: "colorado-springs",
})}`;

const FEATURED_CASE = CASE_STUDIES.find((c) => c.slug === "hvac-colorado-springs")
  ?? CASE_STUDIES[0];

const SPRINGS_REVIEWS = CLIENT_REVIEWS.filter((r) =>
  /Colorado Springs|Monument/i.test(r.location ?? ""),
).slice(0, 4);

export const Route = createFileRoute("/colorado-springs-google-maps")({
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=604800, stale-while-revalidate=2592000",
    "CDN-Cache-Control": "public, s-maxage=604800, stale-while-revalidate=2592000",
  }),
  head: () => ({
    meta: [
      {
        title:
          "Colorado Springs Google Maps SEO — Top-3 Map Pack (2026)",
      },
      {
        name: "description",
        content:
          "Top-3 Google Map Pack rankings for Colorado Springs businesses. Owner-led GBP, citations & reviews from a real Woodmen Rd office. Since 2006.",
      },
      {
        property: "og:title",
        content: "Colorado Springs Google Maps SEO — Top-3 Map Pack (2026)",
      },
      {
        property: "og:description",
        content:
          "Top-3 Map Pack for Springs businesses. Owner-led GBP, citations & reviews. Briargate to Black Forest — since 2006.",
      },
      { property: "og:url", content: "https://coloradowebimpressions.com/colorado-springs-google-maps" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: "Colorado Springs Map Pack — Top-3 Rankings",
        description:
          "GBP, citations, and reviews tuned for the Springs Map Pack.",
      }),
    ],
    links: [
      {
        rel: "canonical",
        href: "https://coloradowebimpressions.com/colorado-springs-google-maps",
      },
    ],
  }),
  component: () => (
    <ServicePage
      slug="google-maps"
      serviceName="Google Maps Ranking"
      pricingRoute="/pricing"
      eyebrow="Colorado Springs Google Maps"
      title="Colorado Springs Google Maps optimization that wins the 3-pack"
      goldWord="3-pack"
      description="Owner-led Google Maps optimization for Colorado Springs businesses — built to win the Briargate, Falcon, Monument, and Black Forest Map Packs and stay there."
      intro={{
        heading:
          "Springs Map Pack rankings aren't won with a generic 'Colorado Springs' GBP.",
        body: [
          "Most Colorado Springs businesses miss the Map Pack because their Google Business Profile is half-built and their reviews are stale. A 'plumber Briargate' search and a 'plumber Old Colorado City' search return two completely different top-3 lineups. We rebuild your GBP from the ground up, fix every NAP inconsistency in Springs citations, and install a sustainable review-velocity system tied to your invoicing.",
          "We're headquartered in Colorado Springs and serve businesses across Colorado Springs, Castle Rock, Monument, Parker, Fountain, Pueblo, and the broader Front Range. Most engagements start in the Springs Map Pack and expand into surrounding markets — but the geo-grid work is always tuned to the specific neighborhoods you actually serve.",
          "Recency beats raw review count. A Springs business with 40 reviews in the last 6 months will typically outrank one with 200 reviews from three years ago. We build a steady review cadence — not a one-time spike that fades.",
        ],
      }}
      benefits={[
        "Full Google Business Profile rebuild — every field tuned for Springs Map Pack",
        "Neighborhood targeting — Briargate, Falcon, Monument, Black Forest, Powers, Old Colorado City",
        "Citation cleanup across the sources Springs Map Pack actually weights",
        "Review velocity engine tied to your invoicing or job completion",
        "Weekly geo-tagged GBP posts and photos",
        "Monthly grid-based ranking reports — block by block",
      ]}
      features={[
        {
          icon: MapPin,
          title: "Springs GBP Rebuild",
          desc: "Categories, services, attributes, service-area polygons, photos — every field tuned for the Colorado Springs Map Pack.",
        },
        {
          icon: Star,
          title: "Review Velocity Engine",
          desc: "Structured request workflow tied to invoicing — Springs reviews from real customers, in a steady monthly cadence.",
        },
        {
          icon: Camera,
          title: "Photos & Posts",
          desc: "Geo-tagged photos and weekly Springs-relevant Google Posts — the activity signals that separate top-3 from #4–10.",
        },
        {
          icon: MessageSquare,
          title: "Review Responses",
          desc: "Professional, on-brand replies to every Springs review — good or bad — within hours, not days.",
        },
        {
          icon: ShieldCheck,
          title: "Citation Cleanup",
          desc: "We fix your NAP across the citation sources Colorado Springs Map Pack actually weights — not generic national directories.",
        },
        {
          icon: BarChart3,
          title: "Geo-Grid Reports",
          desc: "Monthly grid-based ranking reports for every Springs neighborhood you serve — block by block.",
        },
      ]}
      frameworkHeading="The 3-Layer Springs Map Pack System"
      frameworkIntro="A 5-step system refined across 200+ Colorado Springs Google Business Profiles since 2006 — built for the way Springs neighborhoods, base communities, and PCS season actually search."
      framework={[
        {
          number: "01",
          title: "Profile Audit",
          desc: "Full GBP audit: categories, service areas, NAP consistency, attributes, photos, and Q&A. Fix everything Google penalizes.",
        },
        {
          number: "02",
          title: "Local Relevance",
          desc: "Service-specific descriptions, Springs-specific photos (your Woodmen-corridor office, your team, your trucks), and Google Posts that signal you're a real, active local business.",
        },
        {
          number: "03",
          title: "Citation & NAP Cleanup",
          desc: "Consistent name, address, phone across the directories Google trusts most for Colorado Springs businesses.",
        },
        {
          number: "04",
          title: "Review Velocity",
          desc: "Automated SMS/email request workflow tied to job completion — recency matters as much as quantity in the Springs Map Pack.",
        },
        {
          number: "05",
          title: "Geo-Grid Tracking",
          desc: "Monthly grid reports showing exactly where you rank — block by block — across Briargate, Falcon, Monument, Black Forest, and the rest of your Springs service area.",
        },
      ]}
      pricing={pricing}
      industries={[
        {
          icon: Wrench,
          name: "HVAC Map Pack",
          desc: "Top-3 rankings for 'AC repair Colorado Springs' and 'furnace repair Briargate'.",
        },
        {
          icon: Truck,
          name: "Plumbing Map Pack",
          desc: "Capture emergency 'plumber near me' searches across Briargate, Falcon, and Monument 24/7.",
        },
        {
          icon: HomeIcon,
          name: "Roofing Map Pack",
          desc: "Hail-season Map Pack visibility across Pikes Peak Region — the ROI peak of the year.",
        },
        {
          icon: Hammer,
          name: "General Contractors",
          desc: "Earn Map Pack trust + clicks for high-ticket remodel and build queries in Briargate and Black Forest.",
        },
        {
          icon: Trees,
          name: "Landscaping Map Pack",
          desc: "Seasonal Springs rankings for lawn, tree, and design queries — neighborhood by neighborhood.",
        },
        {
          icon: Zap,
          name: "Electrical Map Pack",
          desc: "Emergency-electrician Map Pack rankings across Colorado Springs and the base communities.",
        },
      ]}
      faqs={[
        {
          q: "Are you actually based in Colorado Springs?",
          a: "Yes — we're headquartered in Colorado Springs at 3210 E Woodmen Rd STE 110. You can drive over and meet Chris. We've optimized 200+ Springs-area Google Business Profiles since 2006.",
        },
        {
          q: "How long does it take to rank in the Colorado Springs Map Pack?",
          a: "Most Colorado Springs categories see meaningful Map Pack movement in 60–90 days of consistent GBP optimization, citation cleanup, and review velocity. Highly competitive verticals like HVAC and plumbing during peak season can take 4–6 months to break the top three.",
        },
        {
          q: "How many Google reviews do I need to rank in the Springs?",
          a: "There's no magic number — recency matters as much as quantity. A Springs business with 50 reviews in the last 6 months will typically outrank a competitor with 200 reviews from 3 years ago. We build a steady monthly review cadence.",
        },
        {
          q: "Will you manage my Google Business Profile for me?",
          a: "Yes. Full GBP management is included in every Springs Google Maps engagement — weekly posts, photo uploads, review responses, Q&A monitoring, and category tuning. We also handle citation cleanup across the sources that actually move Springs rankings.",
        },
        {
          q: "Can you help me rank in multiple Colorado Springs neighborhoods?",
          a: "Yes — and it's usually the right move. We build dedicated landing pages and tune your GBP for the actual sub-markets you serve: Briargate, Falcon, Monument, Black Forest, Powers Corridor, Old Colorado City, and the base communities. Each one ranks separately.",
        },
        {
          q: "Do you also serve Castle Rock, Monument, Parker, and Pueblo?",
          a: "Yes. We're headquartered in Colorado Springs but most clients also serve Monument, Tri-Lakes, Falcon, Fountain, and sometimes Castle Rock, Parker, or Pueblo. We map your Map Pack work to the actual ZIPs and cities you serve — not a generic 'Colorado' setup.",
        },
        {
          q: "How is Google Maps ranking different from regular SEO?",
          a: "Map Pack ranking uses different signals than organic — proximity to the searcher, GBP completeness, review velocity, photos, and local citations matter much more than backlinks. It's a parallel discipline, and we build them together.",
        },
        {
          q: "Do you guarantee #1 Map Pack rankings?",
          a: "No ethical agency does — Google forbids it. What we DO guarantee is transparent geo-grid reporting and the same proven playbook that's gotten 200+ Colorado Springs GBPs into the top three.",
        },
      ]}
      sectionsAfterPricing={<SpringsLocalProof />}
    />
  ),
});

function SpringsLocalProof() {
  return (
    <section className="bg-section-alt py-20 lg:py-28" aria-labelledby="springs-maps-proof-heading">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
            Real Colorado Springs Map Pack results
          </span>
          <h2 id="springs-maps-proof-heading" className="mt-5 text-4xl font-extrabold text-foreground sm:text-5xl">
            What <span className="text-gradient-gold">Springs Map Pack</span> dominance looks like
          </h2>
        </div>

        <div className="mt-12 overflow-hidden rounded-2xl border border-brand-gold/40 bg-card p-8 shadow-card sm:p-10">
          <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                Springs case study
              </div>
              <h3 className="mt-3 text-2xl font-extrabold text-foreground sm:text-3xl">
                {FEATURED_CASE.headline}
              </h3>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                {FEATURED_CASE.summary}
              </p>
              {FEATURED_CASE.quote && (
                <blockquote className="mt-5 border-l-4 border-brand-gold pl-4 text-base italic text-foreground/90">
                  "{FEATURED_CASE.quote.text}"
                  <footer className="mt-2 text-sm font-semibold not-italic text-muted-foreground">
                    — {FEATURED_CASE.quote.attribution}
                  </footer>
                </blockquote>
              )}
            </div>
            <div className="rounded-xl bg-gradient-gold p-6 text-center text-brand-black">
              <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-80">
                Result
              </div>
              <div className="mt-2 text-3xl font-black sm:text-4xl">
                {FEATURED_CASE.heroResult}
              </div>
            </div>
          </div>
        </div>

        {SPRINGS_REVIEWS.length > 0 && (
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {SPRINGS_REVIEWS.map((r) => (
              <figure key={r.author} className="rounded-2xl border border-border bg-card p-6 shadow-card">
                <blockquote className="text-sm leading-relaxed text-foreground/90">
                  "{r.body}"
                </blockquote>
                <figcaption className="mt-4 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
                  {r.author}
                  {r.location ? ` — ${r.location}` : ""}
                </figcaption>
              </figure>
            ))}
          </div>
        )}

        <div className="mt-10 text-center">
          <Link
            to="/contact"
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Get a free Springs Map Pack audit
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}
